import { DisabledAccessPotential, ExplorationPotential } from "../../models";
import { WaterType } from "../../models";
import { TriState } from "../../models";
import { IMedia, IPlace } from "../models/Models";
import { InfoBadgeData, InfoBadgeType } from "./InfoBadges";
import { Kernel } from "../Kernel";
import { groupAccessToProtectionLevel } from "../User";
import { formatDistance } from "../Format";
import { getMediaOfType, loadGeoJsonFile } from "../Media";
import { GEO_MEDIA_TYPES } from "../Media";
import { Geo } from "../geo/Geo";
import { getPlaceDiveRating, getPlaceWaterDepthText, getPlaceWaterTempText } from "../PlaceUtils";
import { Difficulty, EntryType } from "../../API";

export async function makePlaceInfoBadges(place: IPlace, media: IMedia[]) {
    const newBadges: InfoBadgeData[] = [];

    if (Kernel.instance().user?.isAdmin()) {
        newBadges.push({
            title: "Collections",
            text: [
                place.scubaCollection && place.scubaCollection === "true" ? "Scuba" : undefined,
                place.caveCollection && place.caveCollection === "true" ? "Cave" : undefined,
            ]
                .filter((x) => x)
                .join(", "),
            type: InfoBadgeType.Info,
        });
    }

    if (place.data.spelioData) {
        if (place.data.spelioData.verticalAccess === TriState.YES) {
            newBadges.push({
                title: "Vertical Access",
                text: "Required",
                type: InfoBadgeType.Warning,
            });
        }

        if (place.data.spelioData.surveyedLength) {
            newBadges.push({
                title: "Reported Length",
                text: formatDistance(place.data.spelioData.surveyedLength),
                type: InfoBadgeType.Info,
            });
        }
    }

    if (media) {
        const geoFiles = getMediaOfType(media, GEO_MEDIA_TYPES);
        let survey: IMedia | undefined;

        if (geoFiles.length === 1) {
            survey = geoFiles[0];
        } else {
            //TODO: allowing marking one file as primary to be used for this as well as loading in cave planner.
            survey = geoFiles[0];
        }
        if (survey) {
            const geoJson = (await loadGeoJsonFile(survey)) as GeoJSON.FeatureCollection;
            if (geoJson) {
                const geo = new Geo(geoJson);
                const surveyedLength = geo.totalLength();
                if (surveyedLength && surveyedLength > 0) {
                    newBadges.push({
                        title: "Geo survey Length",
                        text: formatDistance(surveyedLength),
                        type: InfoBadgeType.Info,
                    });
                }
                const maxDepth = geo.maxDepth();
                if (maxDepth) {
                    newBadges.push({
                        title: "Geo survey depth",
                        text: formatDistance(Math.abs(maxDepth)),
                        type: InfoBadgeType.Info,
                    });
                }
            }
        }
    }

    if (place.data.spelioData.goesToWater === TriState.YES) {
        const diveRatingText = getPlaceDiveRating(place);
        if (diveRatingText) {
            newBadges.push({
                title: "Dive rating",
                text: diveRatingText,
                type: InfoBadgeType.Info,
            });
        }

        const diveData = place.data.diveData;
        if (diveData) {
            const depthText = getPlaceWaterDepthText(place);
            if (depthText) {
                newBadges.push({
                    title: "Water depth",
                    text: depthText,
                    type: InfoBadgeType.Info,
                });
            }

            newBadges.push({
                title: "Water type",
                text: diveData.waterType?.toString(),
                type: InfoBadgeType.Info,
            });
            if (diveData.waterType !== WaterType.FRESH) {
                newBadges.push({
                    title: "Is tidal",
                    text: diveData.isTidal,
                    type: InfoBadgeType.Info,
                });
            }

            // water temp
            const waterTempText = getPlaceWaterTempText(place);
            if (waterTempText) {
                newBadges.push({
                    title: "Water temp",
                    text: waterTempText,
                    type: InfoBadgeType.Info,
                });
            }

            if (diveData.entryType && diveData.entryType !== EntryType.Unknown) {
                newBadges.push({
                    title: "Entry type",
                    text: diveData.entryType,
                    type: InfoBadgeType.Info,
                });
            }
            if (diveData.difficulty && diveData.difficulty !== Difficulty.Unknown) {
                newBadges.push({
                    title: "Difficulty",
                    text: diveData.difficulty,
                    type: InfoBadgeType.Info,
                });
            }
            if (diveData.entryDifficulty && diveData.entryDifficulty !== Difficulty.Unknown) {
                newBadges.push({
                    title: "Entry difficulty",
                    text: diveData.entryDifficulty,
                    type: InfoBadgeType.Info,
                });
            }
        } else {
            // Only show if it hasn't already displayed some dive data which implies this.
            newBadges.push({
                title: "Goes to water",
                text: place.data.spelioData.goesToWater,
                type: InfoBadgeType.Info,
            });
        }
    }

    if (place.data.spelioData.reference) {
        newBadges.push({
            title: "Reference",
            text: place.data.spelioData.reference,
            type: InfoBadgeType.Info,
        });
    }

    if (place.data.spelioData.explorationPotential !== ExplorationPotential.UNKNOWN) {
        newBadges.push({
            title: "Exploration",
            text: place.data.spelioData.explorationPotential,
            type: InfoBadgeType.Info,
        });
    }

    if (place.data.spelioData.disabledAccessPotential !== DisabledAccessPotential.UNKNOWN) {
        newBadges.push({
            title: "Disabled access",
            text: place.data.spelioData.disabledAccessPotential,
            type: InfoBadgeType.Info,
        });
    }

    if (Kernel.instance().user?.inGroup("private")) {
        newBadges.push({
            title: "Privacy",
            text: groupAccessToProtectionLevel(place.groupAccess),
            type: InfoBadgeType.Info,
        });
    }

    return newBadges;
}
